import { MathUtils } from '@/utils/math.utils';

function getDayNoHours(date: string) {
  return `${date.split(',')[0]},${date.split(',')[1]}`;
}
function getOuterWidth(el: HTMLElement) {
  if (!el) return 0;
  const rect = el.getBoundingClientRect();
  // rect.width includes border and padding; if the tooltip can scale, we prefer offsetWidth
  return el.offsetWidth || rect.width;
}

function getOuterHeight(el: HTMLElement) {
  if (!el) return 0;
  const rect = el.getBoundingClientRect();
  return el.offsetHeight || rect.height;
}

function getViewport() {
  return {
    width: Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0),
    height: Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0),
  };
}

function getLocale(language: string) {
  switch (language) {
    case 'es':
      return 'es-ES';
    default:
      return 'en-US';
  }
}

export function generateTooltipContent(
  context: any,
  tooltipTitles: { [key: string]: string },
  averages: {
    nomination: number;
    enagas: number;
  },
  language: string
) {
  // Create/get the container
  let el = document.getElementById('chartjs-tooltip');
  if (!el) {
    el = document.createElement('div');
    el.id = 'chartjs-tooltip';
    el.style.position = 'absolute';
    el.style.pointerEvents = 'none';
    el.style.background = '#212121CC';
    el.style.opacity = '1';
    el.style.border = '1px solid #212121CC';
    el.style.borderRadius = '4px';
    el.style.color = 'white';
    el.style.padding = '8px 10px';
    document.body.appendChild(el);
  }

  const { tooltip } = context;

  if (!tooltip || tooltip.opacity === 0) {
    el.style.opacity = '0';
    return;
  }

  const date = getDayNoHours(tooltip.title?.[0] || '');

  // Build the content with HTML

  const title =
    date.length > 0
      ? new Intl.DateTimeFormat(getLocale(language), {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }).format(new Date(date))
      : '';

  // Split data points into columns of 6 if there are more than 6
  const { dataPoints } = tooltip;
  const itemsPerColumn = 6;
  let dataPointsHtml = '';

  // Create multiple columns, improves bigger data render
  const columns: any[][] = [];
  for (let i = 0; i < dataPoints.length; i += itemsPerColumn) {
    columns.push(dataPoints.slice(i, i + itemsPerColumn));
  }

  dataPointsHtml = `
      <div style="display:flex;gap:16px;">
        ${columns
          .map(
            (column) => `
          <div>
            ${column
              .map((dp: any) => {
                const isNull = dp.raw?.isNull;
                const rawValue = dp.raw?.y;
                const value = isNull ? '-' : MathUtils.formatNumber(rawValue, 0, 0);
                return `
              <div>
                <span style="display:inline-block;width:10px;height:10px;background:${dp.dataset.borderColor};margin-right:6px;border-radius:2px;"></span>
                <strong>${dp.dataset.label}: ${value}</strong>
              </div>
            `;
              })
              .join('')}
          </div>
        `
          )
          .join('')}
      </div>
    `;

  el.innerHTML = `
          <div style="margin-bottom:6px;"><strong>${title}</strong></div>
          ${dataPointsHtml}
          <hr />
          <div style="margin-top:6px;">
            <span style="display:inline-block;width:10px;height:10px;background:#0ea5e9;margin-right:6px;border-radius:2px;"></span><strong>${
              tooltipTitles.averageNomination
            }: ${MathUtils.formatNumber(averages.nomination, 2, 2, true)}</strong>
          </div>
          <div style="margin-top:6px;">
            <span style="display:inline-block;width:10px;height:10px;background:
          #a78bfa;margin-right:6px;border-radius:2px;"></span><strong>${
            tooltipTitles.averageEnagas
          }: ${MathUtils.formatNumber(averages.enagas, 2, 2, true)}</strong>
          </div>
        `;

  // Position
  const { canvas } = context.chart;
  const position = canvas.getBoundingClientRect();
  const { parentElement } = canvas;
  const parentBounds = parentElement ? parentElement.getBoundingClientRect() : position;

  const viewport = getViewport();

  let left = position.left + window.pageXOffset + tooltip.caretX;
  let top = position.top + window.pageYOffset + tooltip.caretY;

  // Show to measure real dimensions of the tooltip
  el.style.opacity = '1';
  el.style.left = '0px';
  el.style.top = '0px';

  const elW = getOuterWidth(el);
  const elH = getOuterHeight(el);

  // Horizontal adjustment: if it goes out on the right, place it to the left of the caret
  if (left + elW >= window.pageXOffset + viewport.width - 4) {
    left -= elW;
  }
  // Left edge
  if (left < window.pageXOffset + 4) {
    left = window.pageXOffset + 4;
  }

  // Vertical adjustment based on parent element bounds
  // Default: place below the caret
  const belowCaretTop = top + 8;
  const aboveCaretTop = top - elH - 8;

  const parentBottom = parentBounds.top + window.pageYOffset + parentBounds.height;

  // Check if tooltip would overflow at the bottom of parent when placed below
  if (belowCaretTop + elH >= parentBottom - 8) {
    // Try to place it above the caret
    top = aboveCaretTop;
  } else {
    // Place below
    top = belowCaretTop;
  }

  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
}
